import type { EnvironmentConfig } from "@reco/shared/src/types/config";
import type { RecommendationsArgs, RecommendationsConfig } from "@reco/shared/src/types/recommendation";

import { PageType } from "@reco/service/src/utils/pageType";
import { ColorScheme, FlexibleScenario, Scenario } from "@reco/shared/src/constants";

const PRODUCT_ID_LENGTH = 9;
const BRAND_NAME_REGEX = /^\/[^/]+\/(?:[a-z]{2}-[a-z]{2}\/)?([^/]+)/i;

function getExcludedProducts(exclude: string[] | undefined): string[] {
  if (!exclude) {
    return [];
  }

  return exclude
    .map((erpNumber) => erpNumber.replaceAll(/\D/g, "").substring(0, PRODUCT_ID_LENGTH))
    .filter((erpNumber) => erpNumber.length === PRODUCT_ID_LENGTH);
}

function getScenario(scenarioArg?: string): Scenario {
  if (!scenarioArg) {
    throw new Error("[RecommendationService] Scenario must be provided");
  }

  const normalizedKey = scenarioArg.toUpperCase();
  const scenario = Scenario[normalizedKey] || FlexibleScenario[normalizedKey];

  if (!scenario) {
    throw new Error(`[RecommendationService] Scenario '${scenarioArg}' does not exist in the list of scenarios`);
  }

  return scenario;
}

function extractProductId(pathname: string): string | undefined {
  const lastSegment = pathname.split("/").pop();
  if (!lastSegment) {
    return undefined;
  }
  const productId = lastSegment.replaceAll(/\D/g, "").substring(0, PRODUCT_ID_LENGTH);
  return productId || undefined;
}

function extractBrandName(pathname: string): string | undefined {
  const match = BRAND_NAME_REGEX.exec(pathname);
  if (!match?.[1]) {
    return undefined;
  }
  return match[1].toLowerCase().replaceAll(/[^a-z0-9]+/gi, "");
}

export function getDefaultParams(config: EnvironmentConfig, recoArgs: RecommendationsArgs): RecommendationsConfig {
  return {
    recoApiPost: config.recoApiPost,
    countryId: config.countryId,
    contextId: recoArgs.contextId,
    displayLimit: config.displayLimit,
    exclude: getExcludedProducts(recoArgs.exclude),
    languageId: config.languageId,
    locale: `${config.languageId}_${config.countryId}`,
    pathname: config.pathname,
    pageType: recoArgs.pageType || config.pageType,
    pageId: recoArgs.pageId,
    recoHost: recoArgs.recoHost || config.recoHost,
    env: config.env,
    recoDetailsHost: recoArgs.recoDetailsHost,
    recommendations: [],
    recommendationItems: [],
    requestLimit: config.requestLimit,
    scenario: getScenario(recoArgs.type),
    min: recoArgs.min && recoArgs.min.toString().toLowerCase() === "true",
    type: recoArgs.type,
    colorScheme: recoArgs.colorScheme == ColorScheme.LIGHT ? ColorScheme.LIGHT : ColorScheme.DARK,
    omnichannel: recoArgs.omnichannel,
    onSale: recoArgs.onSale,
    brandIds: recoArgs.brandIds,
    categoryIds: recoArgs.categoryIds,
    campaignIds: recoArgs.campaignIds,
  };
}

export function getContextId(pageType: string, pathname: string): string | undefined {
  switch (pageType) {
    case PageType.PDP:
    case PageType.RDP:
      return extractProductId(pathname);

    case PageType.Static:
    case PageType.Campaign:
      return extractBrandName(pathname);

    default:
      return undefined;
  }
}

export function getProductIdForCriteoRequest(pageType: string, pathname: string): string | undefined {
  if (pageType !== PageType.PDP) return undefined;
  const match = /\/p(\d+)[^/]*$/.exec(pathname);

  return match ? match[1] : undefined;
}
